<template>
  <CatalogoPagina
    titulo="Modelos"
    prefijo="modelos"
    :resumen="resumen"
    :columnas="columnas"
    :total="total"
    :pagina="paginaActual"
    :total-paginas="totalPaginas"
    :limite="limite"
    :cargando="cargando"
    :ya-cargo="yaCargo"
    :error="errorCarga"
    mensaje-error="No pudimos cargar los modelos."
    :orden-campo="ordenCampo"
    :orden-asc="ordenAsc"
    :aviso="aviso"
    @limite="alCambiarLimite"
    @ordenar="ordenarPor"
    @pagina="cambiarPagina"
    @reintentar="initialize"
    @cerrar-aviso="aviso = ''"
  >
    <template #acciones>
      <button type="button" class="rm-btn rm-btn-secundario" data-testid="modelos-fusionar" @click="pedirFusion()">
        <AppIcon name="arrows-right-left" size="h-4 w-4" />
        Fusionar
      </button>
      <button type="button" class="rm-btn rm-btn-primario" data-testid="modelos-nuevo" @click="nuevoModelo">
        <AppIcon name="plus" size="h-4 w-4" />
        Nuevo modelo
      </button>
    </template>

    <template #filtros>
      <AppBusqueda
        v-model="busqueda"
        placeholder="Buscar modelos"
        testid="modelos-buscar"
        @buscar="aplicarFiltros"
      />

      <div class="mo-filtro-marca">
        <label class="sr-only" for="modelos-filtro-marca">Marca</label>
        <AppCombobox
          id="modelos-filtro-marca"
          :value="marcaFiltro"
          :options="opcionesFiltroMarca"
          placeholder="Busca una marca"
          testid="modelos-marca"
          @input="cambiarMarca"
        >
          <template #opcion="{ opcion }">
            <span class="mo-opcion">
              <MarcaLogo v-if="opcion.nombre" :nombre="opcion.nombre" chico />
              <span class="mo-opcion-texto">{{ opcion.label }}</span>
            </span>
          </template>
        </AppCombobox>
      </div>

      <button
        v-if="hayFiltros"
        type="button"
        class="cat-limpiar"
        data-testid="modelos-limpiar-filtros"
        @click="limpiarFiltros"
      >
        Limpiar filtros
      </button>
    </template>

    <template #filas>
      <tr v-for="modelo in modelos" :key="modelo._id" class="mo-fila" data-testid="modelo-fila">
        <td class="mo-celda-modelo">
          <span class="cat-nombre-texto">{{ nombreDeModelo(modelo.name) }}</span>
        </td>
        <td class="mo-celda-marca">
          <div class="cat-nombre">
            <MarcaLogo :nombre="marcaDe(modelo)" chico />
            <span class="mo-marca-texto">{{ nombreDeMarca(marcaDe(modelo)) }}</span>
          </div>
        </td>
        <td class="mo-celda-conteo mo-celda-autos">
          <template v-if="modelo.autos">
            {{ cifra(modelo.autos) }}<span class="mo-unidad"> {{ modelo.autos === 1 ? 'auto' : 'autos' }}</span>
          </template>
          <span v-else class="cat-sin-dato"><span class="mo-guion" aria-hidden="true">—</span><span class="mo-sin">Sin autos</span></span>
        </td>
        <td class="mo-celda-conteo mo-celda-anios" :class="{ vacia: !rangoAnios(modelo) }">
          <template v-if="rangoAnios(modelo)">{{ rangoAnios(modelo) }}</template>
          <span v-else class="cat-sin-dato"><span aria-hidden="true">—</span><span class="mo-sin">Sin años registrados</span></span>
        </td>
        <td class="cat-fila-acciones">
          <button
            type="button"
            class="cat-icono-btn"
            :aria-label="'Editar ' + nombreDeModelo(modelo.name)"
            data-testid="modelo-editar"
            @click="editarModelo(modelo)"
          >
            <AppIcon name="pencil" size="h-4 w-4" />
          </button>
          <!-- Con autos no se puede eliminar: se ve apagado y al abrirlo explica por qué -->
          <button
            type="button"
            class="cat-icono-btn peligro"
            :class="{ bloqueado: modelo.autos > 0 }"
            :aria-label="'Eliminar ' + nombreDeModelo(modelo.name)"
            :title="modelo.autos > 0 ? `Tiene ${cifra(modelo.autos)} ${modelo.autos === 1 ? 'auto registrado' : 'autos registrados'}` : null"
            data-testid="modelo-eliminar"
            @click="pedirEliminar(modelo)"
          >
            <AppIcon name="trash" size="h-4 w-4" />
          </button>
        </td>
      </tr>
    </template>

    <template #vacio>
      <AppIcon :name="busqueda.trim() ? 'search' : 'tag'" size="h-8 w-8" />
      <p v-if="busqueda.trim()">No hay modelos que coincidan con “{{ busqueda.trim() }}”.</p>
      <template v-else>
        <p>{{ marcaFiltro ? `Aún no hay modelos de ${etiquetaMarca(marcaFiltro)}.` : 'Aún no hay modelos.' }}</p>
        <button type="button" class="rm-btn rm-btn-primario" @click="nuevoModelo">Registrar el primero</button>
      </template>
    </template>

    <!-- Alta / edición -->
    <BaseModal :visible="dialogoForm" :title="tituloForm" persistent @close="cerrarFormulario">
      <form novalidate class="mo-form" data-testid="modelo-form" @submit.prevent="guardarModelo">
        <AppField
          label="Marca"
          required
          :error="intentoGuardar && !marcaForm ? 'Elige una marca.' : ''"
          testid="modelo-marca"
        >
          <template #default="{ controlId }">
            <AppCombobox
              :id="controlId"
              v-model="marcaForm"
              :options="opcionesMarca"
              placeholder="Busca una marca"
              :invalid="intentoGuardar && !marcaForm"
              testid="modelo-marca"
            >
              <template #opcion="{ opcion }">
                <span class="mo-opcion">
                  <MarcaLogo :nombre="opcion.nombre" chico />
                  <span class="mo-opcion-texto">{{ opcion.label }}</span>
                </span>
              </template>
            </AppCombobox>
          </template>
        </AppField>
        <AppField
          v-model="nombre"
          label="Nombre"
          required
          uppercase
          placeholder="SENTRA"
          :error="intentoGuardar && !nombre.trim() ? 'Requerido.' : ''"
          testid="modelo-nombre"
        />
        <div v-if="duplicado" class="mo-aviso-duplicado" role="status" data-testid="modelo-aviso-duplicado">
          <AppIcon name="exclamation" size="h-4 w-4" />
          <span>
            Ya existe <b>{{ nombreDeModelo(duplicado.name) }}</b>
            <template v-if="duplicado.brand"> en {{ nombreDeMarca(duplicado.brand.name) }}</template>.
            <template v-if="!modeloSeleccionado">Usa ese o cambia el nombre.</template>
          </span>
          <button
            v-if="modeloSeleccionado"
            type="button"
            class="mo-enlace"
            data-testid="modelo-fusionar-duplicado"
            @click="fusionarDesdeFormulario(duplicado._id)"
          >
            Fusionarlos
          </button>
        </div>
        <div class="mo-vista-previa" aria-live="polite">
          <MarcaLogo :nombre="nombreMarcaForm" />
          <span v-if="nombre.trim()">
            {{ nombreDeModelo(nombre) }}<span v-if="nombreMarcaForm" class="mo-vista-marca"> · {{ nombreDeMarca(nombreMarcaForm) }}</span>
          </span>
          <span v-else class="mo-vista-vacia">Así se verá en la lista</span>
        </div>
      </form>
      <template #footer>
        <button
          v-if="modeloSeleccionado"
          type="button"
          class="mo-enlace mo-footer-izq"
          data-testid="modelo-fusionar"
          @click="fusionarDesdeFormulario(duplicado ? duplicado._id : '')"
        >
          Fusionar con otro modelo…
        </button>
        <button type="button" class="rm-btn rm-btn-secundario" @click="cerrarFormulario">Cancelar</button>
        <button
          type="button"
          class="rm-btn rm-btn-primario"
          :disabled="!nombre.trim() || !marcaForm || Boolean(duplicado) || guardando"
          data-testid="modelo-guardar"
          @click="guardarModelo"
        >
          {{ guardando ? 'Guardando…' : 'Guardar' }}
        </button>
      </template>
    </BaseModal>

    <!-- Eliminar: solo modelos sin autos -->
    <BaseModal
      :visible="dialogoEliminar"
      :title="seleccionTieneAutos ? 'No se puede eliminar' : 'Eliminar modelo'"
      @close="cerrarEliminar"
    >
      <p v-if="seleccionTieneAutos" class="cat-confirmacion" data-testid="modelo-eliminar-bloqueado">
        <b>{{ nombreDeModelo(modeloSeleccionado.name) }}</b> tiene {{ cifra(modeloSeleccionado.autos) }}
        {{ modeloSeleccionado.autos === 1 ? 'auto registrado' : 'autos registrados' }}. Si está duplicado,
        fusiónalo con el modelo correcto: sus autos pasan a ese modelo y este se elimina.
      </p>
      <p v-else-if="modeloSeleccionado" class="cat-confirmacion">
        ¿Seguro de eliminar <b>{{ nombreDeModelo(modeloSeleccionado.name) }}</b>? No tiene autos registrados.
      </p>
      <template #footer>
        <button type="button" class="rm-btn rm-btn-secundario" @click="cerrarEliminar">
          {{ seleccionTieneAutos ? 'Cerrar' : 'Cancelar' }}
        </button>
        <button
          v-if="seleccionTieneAutos"
          type="button"
          class="rm-btn rm-btn-primario"
          data-testid="modelo-eliminar-fusionar"
          @click="fusionarDesdeEliminar"
        >
          Fusionar…
        </button>
        <button
          v-else
          type="button"
          class="rm-btn rm-btn-peligro"
          :disabled="guardando"
          data-testid="modelo-confirmar-eliminar"
          @click="confirmarEliminar"
        >
          {{ guardando ? 'Eliminando…' : 'Eliminar' }}
        </button>
      </template>
    </BaseModal>

    <!-- Fusionar: el mismo modelo capturado dos veces -->
    <BaseModal :visible="dialogoFusion" title="Fusionar modelos" @close="cerrarFusion">
      <div class="mo-form" data-testid="modelo-fusion">
        <p class="cat-confirmacion">
          Para cuando el mismo modelo está capturado dos veces: los autos del primero pasan al segundo y el
          primero se elimina del catálogo.
        </p>
        <AppField label="Modelo que se elimina" required testid="modelo-fusion-origen">
          <template #default="{ controlId }">
            <AppCombobox
              :id="controlId"
              v-model="fusionOrigenId"
              :options="opcionesModelos"
              :placeholder="cargandoTodos ? 'Cargando modelos…' : 'Busca el repetido o mal escrito'"
              testid="modelo-fusion-origen"
            >
              <template #opcion="{ opcion }">
                <span class="mo-opcion">
                  <MarcaLogo :nombre="opcion.nombre" chico />
                  <span class="mo-opcion-texto">{{ opcion.texto }}</span>
                  <span class="mo-opcion-detalle">{{ opcion.detalle }}</span>
                </span>
              </template>
            </AppCombobox>
          </template>
        </AppField>
        <div class="mo-invertir-fila">
          <button
            type="button"
            class="mo-enlace mo-invertir"
            :disabled="!fusionOrigenId || !fusionDestino"
            title="El que se elimina pasa a ser el que se queda"
            data-testid="modelo-fusion-invertir"
            @click="invertirFusion"
          >
            <AppIcon name="arrows-up-down" size="h-4 w-4" />
            Invertir
          </button>
        </div>
        <AppField label="Pasa sus autos a" required testid="modelo-fusion-destino">
          <template #default="{ controlId }">
            <AppCombobox
              :id="controlId"
              v-model="fusionDestino"
              :options="opcionesDestino"
              :placeholder="cargandoTodos ? 'Cargando modelos…' : 'Busca el modelo correcto'"
              testid="modelo-fusion-destino"
            >
              <template #opcion="{ opcion }">
                <span class="mo-opcion">
                  <MarcaLogo :nombre="opcion.nombre" chico />
                  <span class="mo-opcion-texto">{{ opcion.texto }}</span>
                  <span class="mo-opcion-detalle">{{ opcion.detalle }}</span>
                </span>
              </template>
            </AppCombobox>
          </template>
        </AppField>
        <p
          v-if="origenElegido && destinoElegido"
          class="mo-resumen-fusion"
          aria-live="polite"
          data-testid="modelo-fusion-resumen"
        >
          <template v-if="origenElegido.autos">
            {{ origenElegido.autos === 1 ? 'El auto' : `Los ${cifra(origenElegido.autos)} autos` }} de
            {{ origenElegido.modelo }} {{ origenElegido.autos === 1 ? 'pasará' : 'pasarán' }} a
            <b>{{ destinoElegido.texto }}</b> y {{ origenElegido.modelo }} se eliminará.
          </template>
          <template v-else>{{ origenElegido.modelo }} no tiene autos; solo se eliminará.</template>
          No se puede deshacer.
        </p>
      </div>
      <template #footer>
        <button type="button" class="rm-btn rm-btn-secundario" @click="cerrarFusion">Cancelar</button>
        <button
          type="button"
          class="rm-btn rm-btn-peligro"
          :disabled="!origenElegido || !destinoElegido || guardando"
          data-testid="modelo-confirmar-fusion"
          @click="confirmarFusion"
        >
          {{ guardando ? 'Fusionando…' : 'Fusionar' }}
        </button>
      </template>
    </BaseModal>
  </CatalogoPagina>
</template>

<script>
/*
 * Modelos — catálogo migrado sobre CatalogoPagina.
 *
 * Nombre y marca (la descripción se dejó de capturar: lo que tenía era
 * transmisión o versión, que son del auto). Cada modelo muestra sus autos
 * registrados y el rango de años de esos autos; arranca de mayor a menor por
 * autos. Paginación, búsqueda (modelo o marca, varias palabras), filtros y
 * orden en el backend (/api/model/paginated).
 *
 * Al capturar se avisa si el nombre ya existe (sin importar espacios ni
 * guiones). Un modelo capturado dos veces se fusiona (botón Fusionar, o desde
 * Editar con el modelo ya elegido): sus autos pasan al modelo correcto y el
 * otro se elimina. Un modelo con autos no se elimina: se ofrece fusionarlo. Desde Marcas se llega filtrado por marca
 * (?marca=<id>).
 */
import ApiService from '@/services/apis'
import AppBusqueda from '@/components/ui/AppBusqueda.vue'
import AppCombobox from '@/components/ui/AppCombobox.vue'
import AppField from '@/components/ui/AppField.vue'
import AppIcon from '@/components/ui/AppIcon.vue'
import BaseModal from '@/components/ui/BaseModal.vue'
import CatalogoPagina, { LIMITE_BASE } from '@/components/catalogo/CatalogoPagina.vue'
import MarcaLogo from '@/components/catalogo/MarcaLogo.vue'
import { nombreDeMarca, nombreDeModelo } from '@/utils/formato'
import { paginaEquivalente } from '@/utils/paginacion'

const TIPO_AUTO = 2
const ESPERA_DUPLICADO = 300
const COLUMNAS = [
  { value: 'name', label: 'Modelo' },
  { value: 'marca', label: 'Marca', ancho: '26%' },
  { value: 'autos', label: 'Autos registrados', ancho: '160px', alinear: 'derecha' },
  { value: 'anios', label: 'Años', ancho: '120px', alinear: 'derecha' },
]
const formatoCifra = new Intl.NumberFormat('es-MX')
const limpiarNombre = (texto) => String(texto || '').trim().replace(/\s+/g, ' ').toUpperCase()

export default {
  name: 'modelos',
  components: { AppBusqueda, AppCombobox, AppField, AppIcon, BaseModal, CatalogoPagina, MarcaLogo },
  data() {
    return {
      columnas: COLUMNAS,
      modelos: [],
      marcas: [],
      totales: { modelos: 0, marcas: 0 },
      busqueda: '',
      marcaFiltro: '',
      ordenCampo: 'autos',
      ordenAsc: false,
      paginaActual: 1,
      total: 0,
      totalPaginas: 1,
      limite: LIMITE_BASE,
      // Arranca cargando: CatalogoPagina mide el skeleton y emite `limite`
      cargando: true,
      yaCargo: false,
      errorCarga: false,
      guardando: false,
      aviso: '',
      // Formulario
      dialogoForm: false,
      modeloSeleccionado: null,
      nombre: '',
      marcaForm: '',
      intentoGuardar: false,
      duplicado: null,
      // Eliminar
      dialogoEliminar: false,
      // Fusionar
      dialogoFusion: false,
      fusionOrigenId: '',
      fusionDestino: '',
      todosLosModelos: null,
      cargandoTodos: false,
    }
  },
  computed: {
    resumen() {
      if (!this.yaCargo && !this.totales.modelos) return ''
      const { modelos, marcas } = this.totales
      return `${this.cifra(modelos)} ${modelos === 1 ? 'modelo' : 'modelos'} en ${marcas} ${marcas === 1 ? 'marca' : 'marcas'}`
    },
    opcionesMarca() {
      return this.marcas
        .map((m) => ({ value: m._id, label: nombreDeMarca(m.name), nombre: m.name }))
        .sort((a, b) => a.label.localeCompare(b.label, 'es'))
    },
    opcionesFiltroMarca() {
      return [{ value: '', label: 'Todas las marcas' }, ...this.opcionesMarca]
    },
    hayFiltros() {
      return Boolean(this.busqueda.trim() || this.marcaFiltro)
    },
    tituloForm() {
      return this.modeloSeleccionado ? 'Editar modelo' : 'Nuevo modelo'
    },
    nombreMarcaForm() {
      const marca = this.marcas.find((m) => m._id === this.marcaForm)
      return marca ? marca.name : ''
    },
    seleccionTieneAutos() {
      return Boolean(this.modeloSeleccionado && this.modeloSeleccionado.autos > 0)
    },
    // Todos los modelos (ya vienen por nombre), con sus autos: así se distinguen
    // dos que se llaman igual ("Tundra · Toyota — 5 autos" / "— sin autos")
    opcionesModelos() {
      return (this.todosLosModelos || []).map((m) => {
        const marca = m.brand ? m.brand.name : ''
        const modelo = nombreDeModelo(m.name)
        const texto = `${modelo} · ${nombreDeMarca(marca)}`
        const detalle = m.autos ? `${this.cifra(m.autos)} ${m.autos === 1 ? 'auto' : 'autos'}` : 'sin autos'
        return {
          value: m._id,
          label: `${texto} — ${detalle}`,
          texto,
          detalle,
          modelo,
          nombre: marca,
          autos: m.autos || 0,
          marcaId: m.brand ? String(m.brand._id) : '',
        }
      })
    },
    // Destinos: todos menos el que se elimina, primero los de su misma marca
    opcionesDestino() {
      const origen = this.origenElegido
      const resto = this.opcionesModelos.filter((o) => o.value !== this.fusionOrigenId)
      if (!origen) return resto
      const misma = (o) => (o.marcaId === origen.marcaId ? 1 : 0)
      return [...resto].sort((a, b) => misma(b) - misma(a))
    },
    origenElegido() {
      return this.opcionesModelos.find((o) => o.value === this.fusionOrigenId) || null
    },
    destinoElegido() {
      return this.opcionesDestino.find((o) => o.value === this.fusionDestino) || null
    },
  },
  watch: {
    nombre() {
      this.revisarDuplicado()
    },
    // Un modelo no se fusiona consigo mismo
    fusionOrigenId(origen) {
      if (origen && origen === this.fusionDestino) this.fusionDestino = ''
    },
  },
  created() {
    // Contadores no reactivos: identifican la consulta más reciente
    this.ultimaConsulta = 0
    this.ultimoDuplicado = 0
    this.limiteListo = false
    // Desde Marcas: /menu/catalogo/modelos?marca=<id>
    const marca = this.$route && this.$route.query && this.$route.query.marca
    if (marca) this.marcaFiltro = String(marca)
    this.cargarMarcas()
  },
  beforeDestroy() {
    clearTimeout(this.avisoTimer)
    clearTimeout(this.duplicadoTimer)
  },
  methods: {
    nombreDeMarca,
    nombreDeModelo,
    cifra(n) {
      return formatoCifra.format(n || 0)
    },
    marcaDe(modelo) {
      return modelo && modelo.brand ? modelo.brand.name : ''
    },
    etiquetaMarca(id) {
      const opcion = this.opcionesMarca.find((o) => o.value === id)
      return opcion ? opcion.label : 'esta marca'
    },
    rangoAnios({ anioMin, anioMax } = {}) {
      if (!anioMin && !anioMax) return ''
      if (!anioMin || !anioMax || anioMin === anioMax) return String(anioMin || anioMax)
      return `${anioMin}–${anioMax}`
    },
    async cargarMarcas() {
      try {
        const res = await ApiService.marcaByFilter({ type: TIPO_AUTO })
        this.marcas = Array.isArray(res && res.data) ? res.data : []
      } catch (err) {
        this.marcas = []
      }
    },
    async initialize() {
      const consulta = ++this.ultimaConsulta
      this.cargando = true
      this.errorCarga = false
      try {
        const filtros = {
          pagina: this.paginaActual,
          limite: this.limite,
          busqueda: this.busqueda.trim(),
          ordenarPor: this.ordenCampo,
          direccion: this.ordenAsc ? 'asc' : 'desc',
        }
        if (this.marcaFiltro) filtros.marca = this.marcaFiltro
        const res = await ApiService.modeloPaginado(filtros)
        // Una respuesta que llega tarde (ya hay una consulta más nueva) se descarta
        if (consulta !== this.ultimaConsulta) return
        if (!Array.isArray(res?.modelos)) throw new Error('Respuesta inválida del servicio')
        this.modelos = res.modelos
        this.total = Number(res.total) || 0
        this.totalPaginas = Math.max(1, Number(res.totalPages) || 1)
        if (res.totales) this.totales = res.totales
        this.yaCargo = true
        if (this.paginaActual > this.totalPaginas) {
          this.paginaActual = this.totalPaginas
          return this.initialize()
        }
      } catch (err) {
        if (consulta !== this.ultimaConsulta) return
        this.errorCarga = true
        this.yaCargo = false
        this.modelos = []
        this.total = 0
      } finally {
        if (consulta === this.ultimaConsulta) this.cargando = false
      }
    },
    alCambiarLimite(nuevo) {
      if (!this.limiteListo) {
        this.limiteListo = true
        this.limite = nuevo
        return this.initialize()
      }
      if (nuevo === this.limite) return
      this.paginaActual = paginaEquivalente(this.paginaActual, this.limite, nuevo)
      this.limite = nuevo
      this.initialize()
    },
    ordenarPor(campo) {
      if (campo === this.ordenCampo) {
        this.ordenAsc = !this.ordenAsc
      } else {
        this.ordenCampo = campo
        // Los textos empiezan de la A; autos y años, del mayor
        this.ordenAsc = campo === 'name' || campo === 'marca'
      }
      this.aplicarFiltros()
    },
    cambiarMarca(marca) {
      if (marca === this.marcaFiltro) return
      this.marcaFiltro = marca || ''
      this.aplicarFiltros()
    },
    limpiarFiltros() {
      this.busqueda = ''
      this.marcaFiltro = ''
      this.aplicarFiltros()
    },
    aplicarFiltros() {
      this.paginaActual = 1
      this.initialize()
    },
    cambiarPagina(pagina) {
      if (pagina < 1 || pagina > this.totalPaginas || pagina === this.paginaActual) return
      this.paginaActual = pagina
      this.initialize()
    },
    // --- Alta / edición ---
    abrirFormulario(modelo) {
      this.modeloSeleccionado = modelo
      this.intentoGuardar = false
      this.duplicado = null
      this.marcaForm = modelo ? (modelo.brand ? String(modelo.brand._id) : '') : this.marcaFiltro
      this.nombre = modelo ? limpiarNombre(modelo.name) : ''
      this.dialogoForm = true
      // El watcher de `nombre` no corre si no cambió: se revisa aquí también
      this.revisarDuplicado()
    },
    nuevoModelo() {
      this.abrirFormulario(null)
    },
    editarModelo(modelo) {
      this.abrirFormulario(modelo)
    },
    cerrarFormulario() {
      this.dialogoForm = false
      this.modeloSeleccionado = null
      this.duplicado = null
      clearTimeout(this.duplicadoTimer)
      this.ultimoDuplicado++
    },
    // Aviso en vivo: el nombre ya existe (sin importar espacios ni guiones)
    revisarDuplicado() {
      clearTimeout(this.duplicadoTimer)
      const consulta = ++this.ultimoDuplicado
      const nombre = limpiarNombre(this.nombre)
      if (!this.dialogoForm || !nombre) {
        this.duplicado = null
        return
      }
      this.duplicadoTimer = setTimeout(async () => {
        const excluir = this.modeloSeleccionado ? this.modeloSeleccionado._id : undefined
        const duplicado = await ApiService.modeloDuplicado(nombre, excluir)
        if (consulta === this.ultimoDuplicado) this.duplicado = duplicado
      }, ESPERA_DUPLICADO)
    },
    async guardarModelo() {
      this.intentoGuardar = true
      const nombre = limpiarNombre(this.nombre)
      if (!nombre || !this.marcaForm || this.duplicado || this.guardando) return
      const editando = Boolean(this.modeloSeleccionado)
      this.guardando = true
      try {
        const datos = { name: nombre, brand: { _id: this.marcaForm } }
        if (editando) {
          await ApiService.modeloEditar(this.modeloSeleccionado._id, datos)
        } else {
          await ApiService.modeloNuevo(datos)
        }
        this.cerrarFormulario()
        this.todosLosModelos = null
        await this.initialize()
        this.mostrarAviso(editando ? 'Modelo editado con éxito' : 'Modelo creado con éxito')
      } catch (err) {
        // El servicio ya muestra el detalle (p. ej. "Ya existe CX-3 en MAZDA")
      } finally {
        this.guardando = false
      }
    },
    // --- Eliminar ---
    pedirEliminar(modelo) {
      this.modeloSeleccionado = modelo
      this.dialogoEliminar = true
    },
    cerrarEliminar() {
      this.dialogoEliminar = false
      this.modeloSeleccionado = null
    },
    async confirmarEliminar() {
      this.guardando = true
      try {
        const res = await ApiService.eliminarModelo(this.modeloSeleccionado._id)
        this.cerrarEliminar()
        this.todosLosModelos = null
        if (this.modelos.length === 1 && this.paginaActual > 1) this.paginaActual--
        await this.initialize()
        this.mostrarAviso(res?.data?.message || 'Modelo eliminado')
      } catch (err) {
        // El servicio ya muestra el motivo
      } finally {
        this.guardando = false
      }
    },
    // --- Fusionar ---
    // Desde la barra llega vacío; desde Editar o Eliminar, con el modelo elegido
    pedirFusion(modelo = null, destino = '') {
      this.fusionOrigenId = modelo ? String(modelo._id) : ''
      this.fusionDestino = destino ? String(destino) : ''
      this.dialogoFusion = true
      this.cargarTodosLosModelos()
    },
    invertirFusion() {
      const origen = this.fusionOrigenId
      this.fusionOrigenId = this.fusionDestino
      this.fusionDestino = origen
    },
    fusionarDesdeFormulario(destino) {
      const modelo = this.modeloSeleccionado
      this.cerrarFormulario()
      this.pedirFusion(modelo, destino)
    },
    fusionarDesdeEliminar() {
      const modelo = this.modeloSeleccionado
      this.cerrarEliminar()
      this.pedirFusion(modelo)
    },
    async cargarTodosLosModelos() {
      if (this.todosLosModelos || this.cargandoTodos) return
      this.cargandoTodos = true
      try {
        const modelos = await ApiService.modeloOpciones()
        this.todosLosModelos = Array.isArray(modelos) ? modelos : []
      } catch (err) {
        this.todosLosModelos = null
      } finally {
        this.cargandoTodos = false
      }
    },
    cerrarFusion() {
      this.dialogoFusion = false
      this.fusionOrigenId = ''
      this.fusionDestino = ''
    },
    async confirmarFusion() {
      if (!this.origenElegido || !this.destinoElegido || this.guardando) return
      const origen = this.origenElegido.modelo
      const destino = this.destinoElegido.texto
      this.guardando = true
      try {
        const res = await ApiService.fusionarModelo(this.fusionOrigenId, this.fusionDestino)
        this.cerrarFusion()
        this.todosLosModelos = null
        await this.initialize()
        const autos = Number(res && res.autos) || 0
        const movidos = autos ? ` (${this.cifra(autos)} ${autos === 1 ? 'auto movido' : 'autos movidos'})` : ''
        this.mostrarAviso(`${origen} se fusionó con ${destino}${movidos}`)
      } catch (err) {
        // El servicio ya muestra el motivo
      } finally {
        this.guardando = false
      }
    },
    mostrarAviso(mensaje) {
      this.aviso = mensaje
      clearTimeout(this.avisoTimer)
      this.avisoTimer = setTimeout(() => {
        this.aviso = ''
      }, 4500)
    },
  },
}
</script>

<style scoped>
/* Filtro de marca: mismo alto y trazo que el buscador */
.mo-filtro-marca {
  width: 220px;
  flex: 0 0 220px;
}
.mo-filtro-marca ::v-deep .cb-input {
  display: block;
  width: 100%;
  min-height: 42px;
  margin: 0;
  padding: 0 36px 0 12px;
  border: 1px solid #e2e8ef;
  border-radius: 11px;
  background: #fff;
  box-shadow: 0 3px 12px rgba(16, 42, 67, 0.04);
  color: #0b1b2e;
  font-family: inherit;
  font-size: 14px;
  outline: none;
}
.mo-filtro-marca ::v-deep .cb-input:focus {
  border-color: #0a4f9c;
  box-shadow: 0 0 0 3px rgba(122, 185, 242, 0.55);
}
.mo-opcion {
  display: flex;
  min-width: 0;
  flex: 1;
  align-items: center;
  gap: 8px;
}
.mo-opcion-texto {
  min-width: 0;
  flex: 1;
}
.mo-opcion-detalle {
  flex: none;
  color: #607187;
  font-size: 12px;
  font-variant-numeric: tabular-nums;
}
.mo-invertir-fila {
  display: flex;
  justify-content: center;
  margin: -8px 0;
}
.mo-invertir {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.mo-enlace.mo-invertir:disabled {
  color: #9aa7b6;
  background: transparent;
  cursor: not-allowed;
}
.mo-enlace:focus-visible {
  outline: 3px solid #7ab9f2;
  outline-offset: 2px;
}
.mo-marca-texto {
  overflow: hidden;
  color: #3d4b5f;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.cat-pagina .cat-tabla td.mo-celda-conteo {
  color: #3d4b5f;
  font-variant-numeric: tabular-nums;
  text-align: right;
  white-space: nowrap;
}
.cat-pagina .cat-tabla td.mo-celda-autos {
  color: #0b1b2e;
  font-weight: 600;
}
/* Con autos no se elimina: apagado, pero se puede abrir para ver por qué */
.cat-pagina .cat-icono-btn.bloqueado {
  opacity: 0.45;
}
/* En escritorio la unidad la dice el encabezado */
.mo-unidad {
  display: none;
}
.mo-sin {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}
.mo-form {
  display: grid;
  gap: 14px;
}
.mo-aviso-duplicado {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: -4px;
  padding: 9px 12px;
  border: 1px solid #f1d9a8;
  border-radius: 12px;
  background: #fffaf0;
  color: #6b4100;
  font-size: 13px;
  line-height: 1.4;
}
.mo-aviso-duplicado > span {
  flex: 1;
}
.mo-enlace {
  padding: 4px 6px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: #0a4f9c;
  font-family: inherit;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
}
.mo-enlace:hover {
  background: #edf3f8;
}
.mo-footer-izq {
  margin-right: auto;
}
.mo-vista-previa {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 12px;
  background: #f3f5f8;
  color: #0b1b2e;
  font-size: 14px;
  font-weight: 600;
}
.mo-vista-marca,
.mo-vista-vacia {
  color: #607187;
  font-weight: 500;
}
.mo-resumen-fusion {
  margin: 0;
  padding: 10px 12px;
  border-radius: 12px;
  background: #fdf2f2;
  color: #7f1d1d;
  font-size: 13px;
  line-height: 1.45;
}
@media (max-width: 700px) {
  /* En móvil buscador y marca van cada uno en su renglón, a todo lo ancho */
  .cat-pagina .cat-busqueda,
  .cat-pagina .cat-busqueda:focus-within,
  .cat-pagina .cat-busqueda.expandida {
    width: 100%;
    min-width: 0;
    flex-basis: 100%;
  }
  .mo-filtro-marca {
    width: 100%;
    flex-basis: 100%;
  }
  .cat-pagina .cat-tabla td.mo-celda-conteo {
    color: #526273;
    font-size: 12px;
    font-weight: 500;
    text-align: left;
  }
  .mo-unidad {
    display: inline;
  }
  .mo-guion {
    display: none;
  }
  .mo-sin {
    position: static;
    width: auto;
    height: auto;
    clip: auto;
  }
  /* Tarjeta: modelo, marca debajo y autos · años en una línea */
  .cat-pagina .cat-tabla tbody tr.mo-fila {
    grid-template-columns: auto minmax(0, 1fr) auto;
    align-items: center;
  }
  .cat-pagina .cat-tabla tr.mo-fila td.mo-celda-modelo,
  .cat-pagina .cat-tabla tr.mo-fila td.mo-celda-marca {
    grid-column: 1 / 3;
  }
  .cat-pagina .cat-tabla tr.mo-fila td.mo-celda-autos {
    grid-row: 3;
    grid-column: 1;
  }
  .cat-pagina .cat-tabla tr.mo-fila td.mo-celda-anios {
    grid-row: 3;
    grid-column: 2;
  }
  .cat-pagina .cat-tabla tr.mo-fila td.mo-celda-anios::before {
    content: '·';
    margin: 0 6px;
    color: #9aa7b6;
  }
  .cat-pagina .cat-tabla tr.mo-fila td.mo-celda-anios.vacia {
    display: none;
  }
  .cat-pagina .cat-tabla tr.mo-fila td.cat-fila-acciones {
    grid-column: 3;
    grid-row: 1 / span 3;
  }
}
</style>
